import { Database, Plus, Server } from "lucide-react";
import { Service } from "../../../../types";
import { Button } from "../../../ui/button";
import { EmptyState } from "../../../ui/empty-state";
import { Skeleton } from "../../../ui/skeleton";
import { ServicesTable } from "./ServicesTable";

interface ServicesListProps {
  services: Service[];
  loading?: boolean;
  selectedEnvironment: any;
  onAddService: () => void;
  onRefresh: () => void;
  onDeploymentStarted: () => void;
}

export function ServicesList({
  services,
  loading,
  selectedEnvironment,
  onAddService,
  onRefresh,
  onDeploymentStarted,
}: ServicesListProps) {
  if (!selectedEnvironment) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="flex items-center text-h4">
              <Server className="mr-2 h-5 w-5" />
              Services
            </h2>
            <Skeleton className="h-3.5 w-40 mt-1" />
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-9 w-64" />
          <div className="rounded-md bg-card">
            <div className="flex items-center gap-6 border-b px-4 py-3">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-3 w-14" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-8" />
            </div>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-6 px-4 py-3 border-b last:border-b-0">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-10" />
                <Skeleton className="ml-auto h-7 w-7 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Rendered as a plain section (no outer Card) so the service cards aren't
  // nested inside another card surface.
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center text-h4">
            <Server className="mr-2 h-5 w-5" />
            Services
          </h2>
          <p className="text-sm text-muted-foreground">
            Services in {selectedEnvironment.name} environment
          </p>
        </div>
        {services.length > 0 && (
          <Button onClick={onAddService} variant="default">
            <Plus className="mr-2 h-4 w-4" />
            Add Service
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-64" />
          <div className="rounded-md bg-card">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-6 px-4 py-3 border-b last:border-b-0">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-10" />
                <Skeleton className="ml-auto h-7 w-7 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      ) : services.length > 0 ? (
        <ServicesTable
          services={services}
          onRefresh={onRefresh}
          onDeploymentStarted={onDeploymentStarted}
        />
      ) : (
        <EmptyState
          icon={Database}
          title="No services yet"
          description={`Create your first service in the ${selectedEnvironment.name} environment to start deploying.`}
          primaryAction={{
            label: "Create your first service",
            icon: Plus,
            onClick: onAddService,
          }}
        />
      )}
    </section>
  );
}
